import { AutoComplete, InputNumber } from 'antd';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { InitialRow } from '@components';
import { CreditCrop as ICreditCrop, Crop } from '@types';
import cls from './CreditCrops.module.css';

interface Props {
  crop: ICreditCrop;
  options: Crop[];
  setCrops: Dispatch<SetStateAction<ICreditCrop[]>>;
  editable?: boolean;
}

export const CreditCrop = ({ crop, setCrops, options, editable }: Props) => {
  const [selectedCrops, setSelectedCrops] = useState<Crop[]>([]);
  const [q, setQ] = useState<string>('');

  const onFinancedCropChange = (_: any, option: any) => {
    if (!editable) return;
    setCrops(prev => prev.map(c => {
      if (c.key === crop.key) {
        return { ...c, financedCropId: option?.value || 0 };
      }
      return c;
    }));
    setQ(option?.label);
  };

  const onSearch = (q: string) => {
    if (q) {
      setSelectedCrops(
        options.filter((c: Crop) => c.nameMx.toLowerCase().includes(q.toLowerCase())),
      );
    } else {
      setSelectedCrops(options);
    }
    setQ(q);
  };

  const onFinancedHaChange = (value: number) => {
    if (!editable) return;
    setCrops(prev => prev.map(c => {
      if (c.key === crop.key) {
        return { ...c, financedHa: +value };
      }
      return c;
    }));
  };

  const onExpectedWeightChange = (value: number) => {
    if (!editable) return;
    setCrops(prev => prev.map(c => {
      if (c.key === crop.key) {
        return { ...c, expectedWeight: +value };
      }
      return c;
    }));
  };

  const onDeliveredWeightChange = (value: number) => {
    if (!editable) return;
    setCrops(prev => prev.map(c => {
      if (c.key === crop.key) {
        return { ...c, deliveredWeight: +value };
      }
      return c;
    }));
  };

  const getSelectedCropLabel = (): string => {
    if (!crop.financedCropId) {
      return crop.financedCrop?.nameMx || '';
    }
    const selectedOption = options.find(option => option.id === crop.financedCropId);
    return selectedOption?.nameMx?.toString() || crop.financedCrop?.nameMx || '';
  };

  useEffect(() => {
    if (setSelectedCrops.length) {
      setSelectedCrops(options);
    }
  }, [options]);

  useEffect(() => {
    if (crop.financedCropId) {
      setQ(getSelectedCropLabel());
    }
  }, [crop]);

  return options.length ? (
    <div className={cls.crop}>
      <InitialRow title="Financed crops:">
        <AutoComplete
          placeholder="Select Crops"
          className={cls.formSelect}
          options={(selectedCrops).map(o => ({ value: o.id, label: o.nameMx }))}
          value={q}
          onSearch={onSearch}
          onSelect={onFinancedCropChange}
          disabled={!editable}
        />
      </InitialRow>
      <InitialRow title="Financed Ha:">
        <InputNumber
          min={0}
          max={1000000}
          className={cls.formInput}
          controls={false}
          value={crop?.financedHa || 0}
          onChange={(value) => onFinancedHaChange(value as number)}
          readOnly={!editable}
        />
      </InitialRow>
      <InitialRow title="Expected tons:">
        <InputNumber
          min={0}
          max={1000000}
          className={cls.formInput}
          controls={false}
          value={crop?.expectedWeight || 0}
          onChange={(value) => onExpectedWeightChange(value as number)}
          readOnly={!editable}
        />
      </InitialRow>
      <InitialRow title="Tons delivered:">
        <InputNumber
          min={0}
          max={1000000}
          className={cls.formInput}
          controls={false}
          value={crop?.deliveredWeight || 0}
          onChange={(value) => onDeliveredWeightChange(value as number)}
          readOnly={!editable}
        />
      </InitialRow>
    </div>
  ) : null;
};
